<template>
    <div class="mb-8 space-y-2 max-w-7xl mx-auto">
        <div class="flex items-center justify-between bg-gray-50 py-2 px-3">
            <h4 class="text-lg text-center font-medium">{{ $t('Place Categories') }}</h4>
            <div class="flex justify-end">
                <button
                    class="bg-blue-500 text-white border border-blue-500 hover:border-blue-600 hover:bg-white hover:text-blue-800 transition duration-150 text-sm rounded px-3 py-2"
                    @click="open = true"
                >
                    {{ $t('Create New Place Category') }}
                </button>
            </div>
        </div>

        <modal @close="closeDeleteModal" :closable="!deleting" :open="deleteModalOpen">
            <div slot="header">
                <h2 class="pt-2 text-xl font-semibold text-red-600">
                    Delete Place Category
                </h2>
            </div>
            <div slot="content">
                <form class="mt-4" @submit.prevent="deletePlaceCategories">
                    <p class="bg-red-100 rounded-sm px-3 py-4">
                        Are you sure you want to delete place category id.
                        <br />
                        <span class="uppercase tracking-wide text-sm font-medium"
                            >This action cannot be reversed!!</span
                        >
                    </p>
                    <p v-if="!!deleteError" class="my-2 error-msg">{{ deleteError }}</p>
                    <div class="px-2 mt-6 mb-2 flex justify-between">
                        <Button
                            :disabled="deleting"
                            @click="closeDeleteModal"
                            class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                            type="button"
                            label="Cancel"
                        />
                        <Button
                            :disabled="deleting"
                            :loading="deleting"
                            class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                            label="Yes Delete"
                        />
                    </div>
                </form>
            </div>
        </modal>
        <modal
            :closable="true"
            @close="closeModal"
            :title="$t('Create New Place Category')"
            :open="open"
        >
            <div slot="content">
                <span
                    class="my-12 flex items-center justify-center w-full border border-transparent bg-green-400 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                    v-if="submitted"
                >
                    <svg
                        class="w-5 h-5 text-white mr-1"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    {{ $t('Submitted') }}</span
                >
                <form v-else class="pt-6 pb-10 space-y-4" @submit.prevent="createPlaceCategory">
                    <div class="md:flex items-center space-y-2 md:space-y-0 md:space-x-3">
                        <section class="flex-1 space-y-1 flex flex-col">
                            <label for="tag" class="text-sm">{{ $t('Name (JP)') }}</label>
                            <input
                                placeholder="関東"
                                :disabled="submitting || submitted"
                                v-model="formTag"
                                name="tag"
                                class="form-input"
                            />
                        </section>
                        <section class="flex-1 space-y-1 flex flex-col">
                            <label for="tagEn" class="text-sm">{{ $t('Name (English)') }}</label>
                            <input
                                placeholder="Kanto"
                                :disabled="submitting || submitted"
                                v-model="formTagEn"
                                name="tagEn"
                                class="form-input"
                            />
                        </section>
                    </div>
                    <section class="space-y-1 flex flex-col">
                        <span
                            v-if="formError"
                            class="bg-red-100 text-center text-sm py-1 px-1 rounded shake text-red-900 max-w-3xl overflow-auto"
                        >
                            {{ this.formError }}
                        </span>
                    </section>
                    <div>
                        <button
                            class="flex items-center justify-center w-full border border-transparent hover:bg-transparent hover:border-blue-500 hover:text-gray-900 transition duration-150 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                            :class="[
                                submitting || !this.formValid
                                    ? 'pointer-events-none bg-gray-200'
                                    : 'bg-blue-500',
                            ]"
                            type="submit"
                        >
                            <spinner v-if="submitting" class="text-white" />
                            <span v-else>{{ $t('Create') }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </modal>
        <ul>
            <li v-for="sc in placeCategories" :key="sc.id"></li>
        </ul>

        <draggable
            class="list-group"
            tag="ul"
            v-model="placeCategories"
            v-bind="dragOptions"
            @end="onReorder"
        >
            <transition-group type="transition" :name="'flip-list'">
                <li v-for="sc in placeCategories" :key="sc.id" class="group flex items-center mb-1">
                    <svg
                        class="h-4 w-4 text-gray-300 group-hover:text-indigo-500 cursor-pointer mr-2"
                        viewBox="0 0 96 96"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                    >
                        <path
                            d="M94.2422,43.7578l-12-12a5.9994,5.9994,0,0,0-8.4844,8.4844L75.5156,42H54V20.4844l1.7578,1.7578a5.9994,5.9994,0,0,0,8.4844-8.4844l-12-12a5.9979,5.9979,0,0,0-8.4844,0l-12,12a5.9994,5.9994,0,0,0,8.4844,8.4844L42,20.4844V42H20.4844l1.7578-1.7578a5.9994,5.9994,0,0,0-8.4844-8.4844l-12,12a5.9979,5.9979,0,0,0,0,8.4844l12,12a5.9994,5.9994,0,1,0,8.4844-8.4844L20.4844,54H42V75.5156l-1.7578-1.7578a5.9994,5.9994,0,0,0-8.4844,8.4844l12,12a5.9979,5.9979,0,0,0,8.4844,0l12-12a5.9994,5.9994,0,0,0-8.4844-8.4844L54,75.5156V54H75.5156l-1.7578,1.7578a5.9994,5.9994,0,1,0,8.4844,8.4844l12-12A5.9979,5.9979,0,0,0,94.2422,43.7578Z"
                        />
                    </svg>
                    <PlaceCategoryCard :category="sc" class="flex-1" />
                </li>
            </transition-group>
        </draggable>

        <spinner v-if="loading" />
    </div>
</template>
<script>
import api from '../lib/api'
import Draggable from 'vuedraggable'
import PlaceCategoryCard from '../components/PlaceCategoryCard.vue'
export default {
    components: {
        PlaceCategoryCard,
        Draggable,
    },
    methods: {
        async onReorder() {
            try {
                await api.updatePlaceCategoriesSorting(this.placeCategories)
            } catch (err) {
                console.log(err)
            }
        },

        async createPlaceCategory() {
            this.submitting = true
            this.loading = true
            this.formError = null
            const payload = {
                tag: this.formTag,
                tagEn: this.formTagEn,
            }
            try {
                await api.createPlaceCategory(payload)
                this.submitted = true
            } catch (err) {
                this.formError = err.message
            }
            this.submitting = false
        },
        closeEditModal() {
            this.updateModalOpen = false
            this.updateError = null
        },
        closeDeleteModal() {
            this.deleteModalOpen = false
            this.deleteError = null
        },
        closeModal() {
            this.formError = null
            this.open = false
            if (this.submitted) {
                this.$router.go()
            }
        },
        resetData() {
            this.formName = ''
        },
        async updatePlaceCategories() {
            this.updating = true
            let err
            try {
                await api.updatePlaceCategory(this.selectedCategory)
            } catch (err) {
                this.updateError = err.message
            }
            this.updating = false
            this.updateModalOpen = false
            if (!err) window.location.reload()
        },
        async deletePlaceCategories() {
            this.deleting = true
            let err
            try {
                await api.deletePlaceCategory(this.selectedCategory)
            } catch (err) {
                this.deleteError = err.message
            }
            this.deleting = false
            this.deleteModalOpen = false
            if (!err) window.location.reload()
        },
        selectionChanged(result) {
            this.selectedPlaces = result.selectedRows.map(r => r.id)
        },
    },
    computed: {
        formValid() {
            return (
                !!this.formTag && this.formTag !== '' && !!this.formTagEn && this.formTagEn !== ''
            )
        },
        editFormName: {
            get() {
                const result = this.formName || (this.selectedCategory && this.selectedCategory.tag)
                return result
            },
            set(v) {
                this.formName = v
            },
        },
        editFormNameEn: {
            get() {
                const result =
                    this.formNameEn || (this.selectedCategory && this.selectedCategory.tagEn)
                return result
            },
            set(v) {
                this.formNameEn = v
            },
        },
    },
    data() {
        return {
            dragOptions: {
                animation: 0,
                group: 'description',
                disabled: false,
                ghostClass: 'ghost',
            },
            placeCategories: [],
            selectedCategory: null,
            loading: true,
            formName: '',
            formNameEn: '',
            formError: null,
            formTag: '',
            formTagEn: '',
            submitting: false,
            deleting: false,
            deleteError: null,
            deleteModalOpen: false,
            updating: false,
            updateError: null,
            updateModalOpen: false,
            submitted: false,
            loadingMore: false,
            open: false,
        }
    },
    async mounted() {
        if (this.$route.query.reload === true) {
            this.$router.push('/place-categories')
        }
        this.placeCategories = await api.getPlaceCategories()
        this.loading = false
    },
}
</script>
